import { useCallback, useState, type ComponentProps } from "react";
import { useSnippetContext } from "../../pages/docs/snippetContext";
import { CliHandoffStep } from "../../pages/welcome/CliHandoffStep";
import { cliRouteOffered } from "../../pages/welcome/cliHandoff";
import { cliHandoffCommand } from "../../pages/welcome/cliHandoffCommand";
import { useSnippetKey } from "../../pages/welcome/useSnippetKey";
import { useWorkspace } from "../../workspaces";
import {
  SetupAgentStep,
  type SetupResult,
} from "../../pages/welcome/SetupAgentStep";
import { ConnectStep } from "../../pages/welcome/ConnectStep";
import { VerifyStep } from "../../pages/welcome/VerifyStep";
import { type WizardStepIndex } from "../../pages/welcome/WizardFrame";

/* Set up → Connect → Verify, wherever the wizard is shown. The flows differ only in what
 * Connect contains: the CLI configures itself, everything else uses a snippet. */
export function AddAgentWizard({
  onFinish,
  onAddAnother,
}: {
  onFinish: (agentId: string) => void;
  /** Offered on the last step of the CLI flow; omit to hide it. */
  onAddAnother?: () => void;
}) {
  const { userId } = useSnippetContext();
  const { workspace } = useWorkspace();

  const [step, setStep] = useState<WizardStepIndex>(0);
  const [setup, setSetup] = useState<SetupResult | null>(null);
  // Held here so stepping back and forward again doesn't throw away the confirmation.
  const [connected, setConnected] = useState(false);
  const markConnected = useCallback(() => setConnected(true), []);

  /* Held rather than derived from the step: deriving it lost the reader's choice when they
   * went back to edit, and Continue returned them to the CLI step they had just declined. */
  const [manual, setManual] = useState(false);

  const command = cliHandoffCommand({
    agentId: setup?.agentId ?? null,
    userId,
    workspaceId: workspace.id,
    kind: setup?.kind,
  });

  const cliOffered =
    setup !== null && cliRouteOffered(setup.kind, setup.agentId);

  const viaCli = cliOffered && !manual;

  const toVerify = useCallback(() => {
    markConnected();
    setStep(2);
  }, [markConnected]);

  const toDashboard = useCallback(() => {
    if (setup !== null) onFinish(setup.agentId);
  }, [onFinish, setup]);

  return (
    <>
      {step === 0 && (
        <SetupAgentStep
          existing={setup}
          onContinue={(result) => {
            setSetup(result);
            setStep(1);
          }}
        />
      )}

      {step === 1 &&
        setup !== null &&
        (viaCli ? (
          <CliHandoffStep
            command={command}
            agentId={setup.agentId}
            connected={connected}
            onConnected={toVerify}
            onSkip={toDashboard}
            onManual={() => setManual(true)}
            onBack={() => setStep(0)}
          />
        ) : (
          <SnippetConnectStep
            keyName={setup.name}
            kind={setup.kind}
            agentId={setup.agentId}
            connected={connected}
            onContinue={toVerify}
            onSkip={toDashboard}
            onUseCli={cliOffered ? () => setManual(false) : undefined}
            onBack={() => setStep(0)}
          />
        ))}

      {step === 2 && setup !== null && (
        <VerifyStep
          onDone={toDashboard}
          onBack={viaCli ? undefined : () => setStep(1)}
          onAddAnother={viaCli ? onAddAnother : undefined}
        />
      )}
    </>
  );
}

/* The snippet needs a key. Setup mints none for an agent the CLI configures, so a reader
 * who turns to the snippet from the CLI step — or retypes the agent on the way back — gets
 * one here. Everyone else already has one, and this passes straight through. */
function SnippetConnectStep({
  keyName,
  ...props
}: ComponentProps<typeof ConnectStep> & { keyName: string }) {
  const state = useSnippetKey(props.agentId, `${keyName} API key`);

  if (state === "error") {
    return (
      <div className="flex flex-col gap-3">
        <p className="t-body text-[13px]" style={{ color: "var(--fg-2)" }}>
          Couldn&apos;t create a key for this agent. Go back and try again, or
          create one from Settings › API keys.
        </p>
        {props.onBack && (
          <button
            type="button"
            onClick={props.onBack}
            className="self-start text-body-sm underline text-fg-2"
          >
            Back
          </button>
        )}
      </div>
    );
  }

  if (state === "minting") {
    return (
      <p className="t-body text-[13px]" style={{ color: "var(--fg-3)" }}>
        Creating a key…
      </p>
    );
  }

  return <ConnectStep {...props} />;
}
